import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Collapse,
  Divider,
  Fade,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import FoundationIcon from '@mui/icons-material/Foundation';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WallIcon from '@mui/icons-material/GridOn';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import RoofingIcon from '@mui/icons-material/Roofing';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Category color scheme
const CATEGORY_COLORS = {
  foundation: '#8B5CF6',
  structure: '#3B82F6',
  walls: '#10B981',
  flooring: '#F59E0B',
  roofing: '#EF4444',
  me: '#6366F1',
  others: '#78716C',
};

const CATEGORY_ICONS = {
  foundation: <FoundationIcon fontSize="small" />,
  structure: <AccountBalanceIcon fontSize="small" />,
  walls: <WallIcon fontSize="small" />,
  flooring: <ViewQuiltIcon fontSize="small" />,
  roofing: <RoofingIcon fontSize="small" />,
  me: <ElectricalServicesIcon fontSize="small" />,
  others: <MoreHorizIcon fontSize="small" />,
};

// SVG house illustration for results header
const HouseResultSVG = () => (
  <svg viewBox="0 0 120 60" style={{ width: '100%', height: '100%' }}>
    {/* House body */}
    <rect x="20" y="25" width="80" height="33" fill="rgba(255,255,255,0.15)" rx="2"/>
    {/* Roof */}
    <polygon points="60,5 15,27 105,27" fill="rgba(255,255,255,0.2)"/>
    <polygon points="60,8 20,27 100,27" fill="rgba(255,255,255,0.12)"/>
    {/* Chimney */}
    <rect x="78" y="10" width="8" height="15" fill="rgba(255,255,255,0.12)" rx="1"/>
    {/* Door */}
    <rect x="50" y="38" width="12" height="20" fill="rgba(255,255,255,0.2)" rx="1"/>
    <circle cx="60" cy="48" r="1" fill="rgba(255,255,255,0.4)"/>
    {/* Windows */}
    <rect x="28" y="32" width="14" height="11" fill="rgba(255,255,255,0.25)" rx="1"/>
    <line x1="35" y1="32" x2="35" y2="43" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
    <line x1="28" y1="37.5" x2="42" y2="37.5" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
    <rect x="72" y="32" width="14" height="11" fill="rgba(255,255,255,0.25)" rx="1"/>
    <line x1="79" y1="32" x2="79" y2="43" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
    <line x1="72" y1="37.5" x2="86" y2="37.5" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
    {/* Garden */}
    <circle cx="14" cy="54" r="5" fill="rgba(255,255,255,0.08)"/>
    <circle cx="106" cy="54" r="5" fill="rgba(255,255,255,0.08)"/>
    {/* Ground */}
    <line x1="5" y1="58" x2="115" y2="58" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
  </svg>
);

const CompleteHomeResults = ({
  results,
  onSave,
  onExportPDF,
  onStartNew,
  onBack,
  isSaved = false,
  language = 'en'
}) => {
  const content = {
    en: {
      title: 'Your Home Estimate',
      estimatedCost: 'Estimated Total Cost',
      perSqft: 'per sqft',
      costBreakdown: 'Cost Breakdown',
      materialList: 'Material Quantities',
      exportPdf: 'Export PDF',
      saveProject: 'Save Project',
      saved: 'Saved',
      newEstimate: 'Start New Estimate',
      concrete: 'Concrete & Cement',
      aggregates: 'Aggregates',
      bricks: 'Bricks & Blocks',
      steel: 'Steel',
      finishing: 'Finishing',
      roofing: 'Roofing',
      ceiling: 'Ceiling',
      bags: 'bags',
      tons: 'tons',
      disclaimer: 'Preliminary estimate for planning purposes. Actual costs may vary ±15% based on site conditions and material prices.',
      storey: 'Storey',
      sqft: 'sqft',
      avg: 'Average',
      range: 'Range',
      summary: 'Summary',
      houseDetails: 'House Details',
      area: 'Area',
      floors: 'Floors',
      type: 'Type'
    },
    my: {
      title: 'Anggaran Rumah Anda',
      estimatedCost: 'Anggaran Jumlah Kos',
      perSqft: 'per kaki persegi',
      costBreakdown: 'Pecahan Kos',
      materialList: 'Kuantiti Bahan',
      exportPdf: 'Eksport PDF',
      saveProject: 'Simpan Projek',
      saved: 'Disimpan',
      newEstimate: 'Anggaran Baru',
      concrete: 'Konkrit & Simen',
      aggregates: 'Agregat',
      bricks: 'Bata & Blok',
      steel: 'Keluli',
      finishing: 'Kemasan',
      roofing: 'Bumbung',
      ceiling: 'Siling',
      bags: 'beg',
      tons: 'tan',
      disclaimer: 'Anggaran awal untuk tujuan perancangan. Kos sebenar mungkin berbeza ±15% bergantung pada keadaan tapak dan harga bahan.',
      storey: 'Tingkat',
      sqft: 'kaki persegi',
      avg: 'Purata',
      range: 'Julat',
      summary: 'Ringkasan',
      houseDetails: 'Butiran Rumah',
      area: 'Luas',
      floors: 'Tingkat',
      type: 'Jenis'
    }
  };

  const t = content[language] || content.en;

  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedMaterials, setExpandedMaterials] = useState({ concrete: true });

  if (!results) return null;

  const { summary, materials, inputs, params } = results;

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `RM ${(value / 1000000).toFixed(2)}M`;
    }
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatRange = (item) => {
    if (item.min === item.max) {
      return item.avg.toLocaleString();
    }
    return `${item.min.toLocaleString()} - ${item.max.toLocaleString()}`;
  };

  const avgCost = Math.round((summary.totalCost.min + summary.totalCost.max) / 2);
  const avgPerSqft = Math.round((summary.perSqft.min + summary.perSqft.max) / 2);

  // Visual cost bar for each category
  const CategoryBar = ({ category, index }) => {
    const catId = category.id;
    const color = Object.values(CATEGORY_COLORS)[index] || CATEGORY_COLORS.others;
    const icon = Object.values(CATEGORY_ICONS)[index] || CATEGORY_ICONS.others;
    const isExpanded = expandedCategories[catId];

    return (
      <Box
        sx={{ mb: 0.5 }}
      >
        <Box
          onClick={() => setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 1,
            cursor: 'pointer',
            borderRadius: 1.5,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {/* Color dot + icon */}
          <Box sx={{
            width: 32, height: 32,
            borderRadius: 1.5,
            bgcolor: color + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            flexShrink: 0,
          }}>
            {icon}
          </Box>

          {/* Label */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
              {language === 'my' ? category.labelMy : category.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: color + '20',
                flex: 1,
                overflow: 'hidden',
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${category.percentage * 4}%`,
                  bgcolor: color,
                  borderRadius: 2,
                  transition: 'width 0.6s ease',
                }} />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color, minWidth: 32, textAlign: 'right' }}>
                {category.percentage}%
              </Typography>
            </Box>
          </Box>

          {/* Amount */}
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatCurrency(category.amount)}
            </Typography>
          </Box>

          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{
            ml: 5.5, mr: 1, mb: 1, px: 1.5, py: 1,
            bgcolor: 'action.hover',
            borderRadius: 1.5,
            borderLeft: `3px solid ${color}`,
          }}>
            <Typography variant="caption" color="text.secondary">
              {t.range}: {formatCurrency(Math.round(category.amount * 0.88))} - {formatCurrency(Math.round(category.amount * 1.12))}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Material section
  const MaterialSection = ({ id, title, items }) => {
    const isExpanded = expandedMaterials[id];
    if (!items || Object.keys(items).length === 0) return null;

    const itemCount = Object.keys(items).length;

    return (
      <Box>
        <Box
          onClick={() => setExpandedMaterials(prev => ({ ...prev, [id]: !prev[id] }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Chip label={itemCount} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
          </Box>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            {Object.entries(items).map(([key, item]) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {language === 'my' ? item.labelMy : item.label}
                  </Typography>
                  {item.tons && (
                    <Typography variant="caption" color="text.secondary">
                      {formatRange(item.tons)} {t.tons}
                    </Typography>
                  )}
                  {item.bundles && (
                    <Typography variant="caption" color="text.secondary">
                      {formatRange(item.bundles)} bundles
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatRange(item)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {language === 'my' ? item.unitMy : item.unit}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 520, mx: 'auto' }}>
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
        >
          {language === 'my' ? 'Kembali' : 'Back'}
        </Button>

        {/* Hero Cost Card */}
        <Paper
          elevation={0}
          sx={{
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #1a1a2e 100%)`,
            color: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 2,
            position: 'relative',
          }}
        >
          {/* House illustration background */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: -10,
            width: 160,
            height: 80,
            opacity: 0.5,
          }}>
            <HouseResultSVG />
          </Box>

          <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            {/* Project info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HomeWorkOutlinedIcon sx={{ fontSize: 20, opacity: 0.9 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                {inputs?.projectName || t.title}
              </Typography>
            </Box>

            {/* Main cost */}
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.02em' }}>
              {formatCurrency(avgCost)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
              {formatCurrency(summary.totalCost.min)} - {formatCurrency(summary.totalCost.max)}
            </Typography>

            {/* Info chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`${params?.builtUpArea?.toLocaleString()} ${t.sqft}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              <Chip
                label={`${params?.floors} ${t.storey}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              <Chip
                label={`RM ${avgPerSqft}/${language === 'my' ? 'kps' : 'sqft'}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              <Chip
                label={inputs?.finishingLevel?.charAt(0).toUpperCase() + inputs?.finishingLevel?.slice(1)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={onExportPDF}
            sx={{
              flex: 1,
              py: 1.2,
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {t.exportPdf}
          </Button>
          <Button
            variant={isSaved ? 'outlined' : 'contained'}
            startIcon={isSaved ? <CheckCircleIcon /> : <SaveIcon />}
            onClick={onSave}
            disabled={isSaved}
            color={isSaved ? 'success' : 'primary'}
            sx={{
              flex: 1,
              py: 1.2,
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {isSaved ? t.saved : t.saveProject}
          </Button>
        </Box>

        {/* Cost Breakdown */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            p: 2,
            mb: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t.costBreakdown}
          </Typography>

          {/* Visual bar chart */}
          <Box sx={{ display: 'flex', gap: 0.5, mb: 2, height: 8, borderRadius: 4, overflow: 'hidden' }}>
            {summary.categories.map((cat, i) => (
              <Box
                key={cat.id}
                sx={{
                  flex: cat.percentage,
                  bgcolor: Object.values(CATEGORY_COLORS)[i] || CATEGORY_COLORS.others,
                  transition: 'flex 0.6s ease',
                  '&:first-of-type': { borderRadius: '4px 0 0 4px' },
                  '&:last-of-type': { borderRadius: '0 4px 4px 0' },
                }}
              />
            ))}
          </Box>

          {/* Category list */}
          {summary.categories.map((category, index) => (
            <CategoryBar key={category.id} category={category} index={index} />
          ))}
        </Paper>

        {/* Material Quantities */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            mb: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t.materialList}
            </Typography>
          </Box>

          <Divider />
          <MaterialSection id="concrete" title={t.concrete} items={materials.concrete} />
          <Divider />
          <MaterialSection id="aggregates" title={t.aggregates} items={materials.aggregates} />
          <Divider />
          <MaterialSection id="bricks" title={t.bricks} items={materials.bricks} />
          <Divider />
          <MaterialSection id="steel" title={t.steel} items={materials.steel} />
          <Divider />
          <MaterialSection id="finishing" title={t.finishing} items={materials.finishing} />
          <Divider />
          <MaterialSection id="roofing" title={t.roofing} items={materials.roofing} />
          <Divider />
          <MaterialSection id="ceiling" title={t.ceiling} items={materials.ceiling} />
        </Paper>

        {/* Disclaimer */}
        <Box sx={{
          display: 'flex',
          gap: 1,
          p: 1.5,
          mb: 2,
          bgcolor: 'action.hover',
          borderRadius: 2,
          alignItems: 'flex-start',
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {t.disclaimer}
          </Typography>
        </Box>

        {/* New Estimate */}
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={onStartNew}
          fullWidth
          sx={{ textTransform: 'none', color: 'text.secondary', mb: 2 }}
        >
          {t.newEstimate}
        </Button>
      </Box>
    </Fade>
  );
};

export default CompleteHomeResults;
